/* ==========================================================================
   Integrated Management System (IMS) Policy Page Component
   Authentic Multilingual Implementation for JALUD Embedded s.r.o.
   Covers ISO 9001:2015 and ISO/IEC 27001:2022
   Languages: CS, DE, EN, ES, PT, AR (with strict RTL support)
   ========================================================================== */

import { getLocale } from '../locales/i18n.js';
import { getSvgIcon } from '../components/Icons.js';
import { exportLegalDocumentToPdf, PDF_BUTTON_LABELS } from '../utils/legalPdfExport.js';

import { imsData, LEGAL_VERSIONS } from '../locales/legalTranslations.js';

export function renderPolicyIMSPage(onNavigate) {
  const container = document.createElement('div');
  container.className = 'sed-page';
  const lang = getLocale() || 'en';
  const data = imsData[lang] || imsData.en;
  const isRtl = lang === 'ar';
  const pdfBtnLabel = PDF_BUTTON_LABELS[lang] || PDF_BUTTON_LABELS.en;

  container.innerHTML = `
    <section class="sed-section" style="background: linear-gradient(165deg, var(--blue-light) 0%, #ffffff 70%); padding: 4.5rem 0 3rem; direction: ${isRtl ? 'rtl' : 'ltr'};">
      <div class="sed-container">
        <div class="sed-section-header" style="max-width: 860px; margin: 0 auto 2rem; text-align: center;">
          <div class="sed-eyebrow">${data.eyebrow}</div>
          <h1 class="sed-page-hero-title">${data.title}</h1>
          <p class="sed-section-desc">${data.subtitle}</p>
        </div>
      </div>
    </section>

    <section class="sed-section" style="background-color: #ffffff; padding: 2rem 0 5rem; direction: ${isRtl ? 'rtl' : 'ltr'}; text-align: ${isRtl ? 'right' : 'left'};">
      <div class="sed-container" style="max-width: 900px;">
        <div class="sed-card" style="padding: 2.75rem; line-height: 1.8; color: var(--text-dark); border: 1.5px solid var(--slate-line); border-radius: 16px; box-shadow: 0 4px 20px rgba(0,0,0,0.03);">
          
          <!-- Top Action Bar (Metadata + PDF Link) -->
          <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid var(--blue-light); padding-bottom: 1.25rem; margin-bottom: 2rem; flex-wrap: wrap; gap: 1rem;">
            <div>
              <span style="font-weight: 800; color: var(--navy-dark); font-size: 1.15rem; font-family: var(--font-heading);">
                JALUD Embedded s.r.o.
              </span><br>
              <span style="font-size: 0.88rem; color: var(--text-muted); font-weight: 600;">
                📅 ${data.dateStr}
              </span>
            </div>
            <div style="display: flex; flex-direction: row; align-items: center; gap: 0.75rem; flex-wrap: wrap;">
              <div style="font-size: 0.82rem; font-weight: 700; color: var(--blue-primary); background: #ffffff; padding: 0.45rem 0.9rem; border-radius: 6px; border: 1px solid var(--slate-line); white-space: nowrap;">
                ${data.version || (LEGAL_VERSIONS && (LEGAL_VERSIONS[lang] || LEGAL_VERSIONS.en)) || 'Verze 2026.1 (Účinnost od 1. ledna 2026)'}
              </div>
              <button class="sed-btn sed-btn-outline sed-download-pdf-btn" id="sed-download-pdf-btn" style="font-size: 0.82rem; padding: 0.45rem 0.9rem; display: inline-flex; align-items: center; gap: 6px; cursor: pointer; border-radius: 6px; background: #ffffff; color: var(--blue-primary); border-color: var(--blue-primary); font-weight: 700; transition: all 0.2s; white-space: nowrap;" onmouseover="this.style.background='var(--blue-ultra-light)'" onmouseout="this.style.background='#ffffff'">
                ${getSvgIcon('download', 14, 'var(--blue-primary)')}
                <span>${pdfBtnLabel}</span>
              </button>
              ${data.pdfUrl ? `
                <a href="${data.pdfUrl}" target="_blank" rel="noopener noreferrer" class="sed-btn sed-btn-outline" style="font-size: 0.88rem; padding: 0.45rem 0.95rem; color: var(--text-muted); border-color: var(--slate-line); font-weight: 600; display: inline-flex; align-items: center; gap: 6px;">
                  <span>📄</span>
                  <span>${data.pdfBtn || 'Scan PDF'}</span>
                </a>
              ` : ''}
            </div>
          </div>

          <!-- The 9 Official IMS Sections -->
          <div style="display: flex; flex-direction: column; gap: 2rem;">
            ${data.sections.map(sec => `
              <div style="border-bottom: 1px solid var(--slate-line); padding-bottom: 1.5rem;">
                <h3 style="font-family: var(--font-heading); font-size: 1.25rem; font-weight: 800; color: var(--navy-dark); margin-bottom: 0.65rem; display: flex; align-items: center; gap: 10px;">
                  <span style="background: var(--blue-ultra-light); color: var(--blue-primary); width: 30px; height: 30px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 0.9rem; font-weight: 800; flex-shrink: 0;">
                    ${sec.num}
                  </span>
                  <span>${sec.title}</span>
                </h3>
                <p style="margin: 0; font-size: 1.02rem; line-height: 1.75; color: var(--text-dark);">
                  ${sec.text}
                </p>
              </div>
            `).join('')}
          </div>

        </div>
      </div>
    </section>
  `;

  // Attach PDF export button handler
  const pdfBtn = container.querySelector('#sed-download-pdf-btn');
  if (pdfBtn) {
    pdfBtn.addEventListener('click', (e) => {
      e.preventDefault();
      exportLegalDocumentToPdf('ims', lang, pdfBtn);
    });
  }

  return container;
}
